import OTPInput from '@/components/OTPInput';
import { zodResolver } from '@hookform/resolvers/zod';
import { CircularProgress, FormControl, InputLabel, MenuItem, Select, SelectChangeEvent } from '@mui/material';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import { useCallback, useState } from 'react';
import { useCookies } from 'react-cookie';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import useJwtAuth from '../useJwtAuth';

/**
 * Form Validation Schema for Social OTP
 */
const socialOtpSchema = z.object({
    otp: z.string().optional(),
    remember_me_duration: z.number().optional(),
    recovery_code: z.string().optional(),
});

type SocialOtpFormType = {
    otp?: string;
    remember_me_duration?: number;
    recovery_code?: string;
};

const defaultSocialOtpValues = {
    otp: '',
    remember_me_duration: 0,
    recovery_code: '',
};

type SocialOtpFormProps = {
    socialIdentifier: string;
    mfaType: 'totp' | 'email' | 'otp' | 'recovery';
    initialType: 'totp' | 'email' | 'otp' | 'recovery';
    onSuccess: () => void;
};

/**
 * This component contains the logic for the Social OTP verification form
 * @returns A React.Node element
 */
function SocialOtpForm({ socialIdentifier, mfaType: initialMfaType, initialType, onSuccess }: Readonly<SocialOtpFormProps>) {
    const { socialOtpSignIn } = useJwtAuth();
    const [loading, setLoading] = useState<boolean>(false);
    const [mfaType, setMfaType] = useState<'totp' | 'email' | 'otp' | 'recovery'>(initialMfaType);
    const [, , removeCookie] = useCookies(['mfa-remember']);

    const { control, formState, handleSubmit, setError } = useForm<SocialOtpFormType>({
        mode: 'onChange',
        defaultValues: defaultSocialOtpValues,
        resolver: zodResolver(socialOtpSchema),
    });

    const { errors } = formState;

    // Handles the submitting of the form
    const onSubmit = useCallback(async (formData: SocialOtpFormType) => {
        const { otp, remember_me_duration, recovery_code } = formData;
        setLoading(true);

        try {
            const request = {
                '2fa_type': mfaType,
                identifier: socialIdentifier,
                otp: otp,
                recovery_code: recovery_code,
                remember_me_duration: remember_me_duration
            };

            await socialOtpSignIn(request);
            onSuccess();
        } catch (error: unknown) {
            const apiError = error as Error & {
                data?: { detail?: string; error?: string; };
            };

            if (apiError.data?.error === 'Invalid Remember Me token') {
                removeCookie('mfa-remember');
            }

            setLoading(false);

            // Handle validation errors
            const errorDetail = apiError.data?.detail;

            let errorMessage = 'Failed to verify. Please try again.';

            if (errorDetail) {
                errorMessage = errorDetail;
            }

            if (mfaType !== 'recovery') {
                // OTP validation errors
                setError('otp', {
                    type: 'manual',
                    message: errorMessage || 'Failed to verify OTP. Please try again.',
                });
            } else {
                // Recovery code errors
                setError('recovery_code', {
                    type: 'manual',
                    message: errorMessage || 'Failed to verify recovery code. Please try again.',
                });
            }
        }
    }, [mfaType, socialIdentifier, socialOtpSignIn, onSuccess, removeCookie, setError]);

    const toggleRecoveryMode = useCallback(() => {
        if (mfaType === 'recovery') {
            setMfaType(initialType);
        } else {
            setMfaType('recovery');
        }
    }, [mfaType, initialType]);

    return (
        <form
            name="socialOtpForm"
            noValidate
            className="mt-12 flex w-full flex-col justify-center space-y-16"
            onSubmit={handleSubmit(onSubmit)}
        >
            <div className="space-y-16">
                <Controller
                    name="remember_me_duration"
                    control={control}
                    render={({ field }) => {
                        return (
                            <FormControl fullWidth>
                                <InputLabel id="remember-me">
                                    2FA Remember Duration
                                </InputLabel>
                                <Select
                                    labelId="remember-me-label"
                                    id="remember-me"
                                    data-testid="remember-me-select"
                                    label="2FA Remember Duration"
                                    value={field.value}
                                    onChange={(e: SelectChangeEvent<number>) =>
                                        field.onChange(e.target.value)
                                    }
                                    {...field}
                                >
                                    <MenuItem value={0}>Don't remember</MenuItem>
                                    <MenuItem value={1}>1 Day</MenuItem>
                                    <MenuItem value={7}>7 Days</MenuItem>
                                    <MenuItem value={31}>31 Days</MenuItem>
                                </Select>
                            </FormControl>
                        );
                    }}
                />

                {(mfaType === 'totp' || mfaType === 'email') && (
                    <OTPInput
                        control={control}
                        name="otp"
                        type={mfaType}
                        errorMessage={errors?.otp?.message as string}
                        isSubmitting={loading}
                    />
                )}

                {mfaType === 'recovery' && (
                    <Controller
                        name="recovery_code"
                        control={control}
                        render={({ field }) => (
                            <TextField
                                {...field}
                                label="Recovery Code"
                                type="text"
                                error={!!errors.recovery_code}
                                helperText={errors?.recovery_code?.message}
                                variant="outlined"
                                required
                                fullWidth
                            />
                        )}
                    />
                )}

                <Button className='float-right' onClick={toggleRecoveryMode}>
                    {mfaType === 'recovery' ? 'Switch back to OTP' : 'Use recovery code'}
                </Button>
            </div>

            <Button
                variant="contained"
                color="primary"
                className="w-full mt-24"
                aria-label="Verify OTP"
                disabled={loading ||
                    (mfaType === 'recovery' && !formState.dirtyFields.recovery_code) ||
                    ((mfaType === 'totp' || mfaType === 'email') && !formState.dirtyFields.otp)
                }
                type="submit"
                size="large"
            >
                {loading && <CircularProgress size={15} className="mr-5" />}
                Verify OTP
            </Button>
        </form>
    );
}

export default SocialOtpForm;
